/**
 * PATH: packages/bills/src/components/bill-document-type.ts
 * FILE_ID: BILLS-PKG-UTIL-DOCTYPE-002
 * PURPOSE: Classify the AI-extracted document and derive the header eyebrow
 *          label + the "needs manual review" predicate for the data panel.
 *
 * WHY: The panel header, empty-extraction callout, and review banner all
 *      key off what KIND of document this is (bill vs statement vs
 *      remittance …). Centralising the classifier keeps those three call
 *      sites in lock-step and testable in isolation.
 *
 * Extracted verbatim from BillDataPanel.tsx (P4-1 split) — pure functions,
 * no behaviour change. BillDataPanel re-exports DocumentType / getDocumentType
 * / documentTypeLabel to preserve its public surface.
 */

import type { BillDetail } from '../types';

/**
 * Document types the AI extractor knows about. Mirrors the prompt
 * vocabulary in
 * ``organisation/services/bill_extractor/service.py::EXTRACTION_SYSTEM_PROMPT``.
 * ``unknown`` only appears for older bills extracted before the
 * 2026-05-03 prompt update, where ``raw_extraction.document_type``
 * is missing.
 */
export type DocumentType =
  | 'tax_invoice'
  | 'statement'
  | 'remittance'
  | 'credit_note'
  | 'unknown';

/**
 * Source-of-truth document classifier. The AI prompt now emits
 * ``document_type`` directly — that's authoritative. We only fall
 * back to filename/subject regex for legacy rows where the field
 * is missing from ``raw_extraction``.
 */
export function getDocumentType(bill: BillDetail): DocumentType {
  const fromAi = bill.raw_extraction?.document_type;
  if (typeof fromAi === 'string') {
    const norm = fromAi.toLowerCase().trim();
    if (
      norm === 'tax_invoice' ||
      norm === 'statement' ||
      norm === 'remittance' ||
      norm === 'credit_note'
    ) {
      return norm;
    }
  }

  const haystack = [
    bill.attachment_filename || '',
    bill.email_subject || '',
  ]
    .join(' ')
    .toLowerCase();
  if (/\bstatement\b|\bsoa\b/.test(haystack)) return 'statement';
  if (/\bremittance\b/.test(haystack)) return 'remittance';
  if (/\bcredit ?note\b|\bcredit memo\b/.test(haystack)) return 'credit_note';
  return 'unknown';
}

/**
 * Display label for the panel header eyebrow + empty-extraction
 * callout. Built on top of ``getDocumentType`` for the four
 * AI-vocabulary types; the filename heuristic is reused for the
 * extra "richness" labels (Receipt / Quote / Purchase order)
 * that aren't part of the AI vocabulary but were useful eyebrow
 * text before the prompt update — keep them so legacy rows look
 * the same as they did before.
 */
export function documentTypeLabel(bill: BillDetail): string {
  const t = getDocumentType(bill);
  switch (t) {
    case 'statement':
      return 'Statement';
    case 'remittance':
      return 'Remittance';
    case 'credit_note':
      return 'Credit note';
    case 'tax_invoice':
      return 'Bill';
    default:
      break;
  }

  const haystack = [
    bill.attachment_filename || '',
    bill.email_subject || '',
  ]
    .join(' ')
    .toLowerCase();
  if (/\breceipt\b/.test(haystack)) return 'Receipt';
  if (/\bquote\b|\bquotation\b/.test(haystack)) return 'Quote';
  if (/\bpurchase order\b|\bpo[\s_-]/.test(haystack)) return 'Purchase order';
  return 'Bill';
}

/**
 * Returns true when the AI ran (status === 'extracted') but came
 * back with no usable financial data. Drives the amber "review
 * manually" callout so users aren't fooled into thinking an empty
 * panel means the bill is processed.
 *
 * Doc-type aware (2026-05-03, jake): statements + remittances
 * intentionally have a null ``invoice_number`` because they
 * aggregate multiple invoices — only ``total`` (closing balance /
 * amount due) is required for them. Tax invoices and credit notes
 * still need both ``total`` AND ``invoice_number`` to be
 * considered usable. Without this branch, every correctly-extracted
 * statement (e.g. ADDLER's customer statement, total $2,924.68 with
 * 3 invoice rows) was incorrectly labelled "AI extraction came
 * back empty".
 */
export function needsManualReview(bill: BillDetail): boolean {
  if (bill.status !== 'extracted') return false;
  const empty = (v: string | null | undefined) => !v || v.trim() === '';
  const noAmounts = empty(bill.total) && empty(bill.subtotal);

  const docType = getDocumentType(bill);
  if (docType === 'statement' || docType === 'remittance') {
    return noAmounts;
  }

  const noInvoiceNumber = empty(bill.invoice_number);
  return noAmounts || noInvoiceNumber;
}
